import {Button} from '@wandb/components';
import {linkify} from '@wandb/weave/common/util/links';
import {Select} from '@wandb/weave/components/Form/Select';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useContext, useEffect} from 'react';

import {useEntityPageQuery} from '../../../../generated/graphql';
import {useViewer} from '../../../../state/viewer/hooks';
import {TeamMemberRole} from '../../../MembersTable/TeamMembersTable/utils';
import {FILE_KEY_DELIMITER, type Property} from '../../utilTypes';
import {JobDrawerSecretsContext} from '../JobDrawerSecretsContext';
import PropertyHeader from './PropertyHeader';

export const SECRET_PREFIX = 'secret://';

type SecretDropdownProps = {
  property: Property;
  value: string | null | undefined;
  onChange: (newValue: any) => void;
  targetEntityName: string;
  openSecretsView?: () => void;
};

type PropertyOption = {
  property: string;
  label?: string;
  value?: string;
};

export const SecretDropdown = ({
  value,
  property,
  onChange,
  targetEntityName,
  openSecretsView,
}: SecretDropdownProps) => {
  const id = (property.file ?? '') + FILE_KEY_DELIMITER + property.key;

  const viewer = useViewer();
  const {data: entityData} = useEntityPageQuery({
    variables: {entityName: targetEntityName},
  });

  const viewerIsTeamAdmin =
    entityData?.entity?.members.find(m => m.id === viewer?.id)?.role ===
    TeamMemberRole.ADMIN;

  const {secretsData, secretsLoading, secretsError} = useContext(
    JobDrawerSecretsContext
  );

  const options: PropertyOption[] =
    secretsLoading || secretsError
      ? []
      : secretsData.map(secret => ({
          property: id,
          label: secret.name,
          value: SECRET_PREFIX + secret.name,
        }));

  const getPlaceholderText = () => {
    if (secretsLoading) {
      return 'Loading secrets...';
    }
    if (secretsError) {
      return 'Error loading secrets';
    }
    if (secretsData.length === 0) {
      return 'No team secrets available';
    }
    return property.placeholder ?? 'Select a secret';
  };

  // Find the current selected option from the options array
  const optionValue: PropertyOption | null =
    secretsLoading || secretsError || !value
      ? null
      : options.find(option => option.value === value) || null;

  useEffect(() => {
    if (
      !secretsLoading &&
      !secretsError &&
      optionValue == null &&
      value != null
    ) {
      onChange({property: id, value: null});
    }
  }, [optionValue, value, onChange, id, secretsLoading, secretsError]);

  const handleChange = (selected: PropertyOption | null) => {
    onChange({property: id, value: selected?.value});
  };

  const isCreateSecretDisabled = !targetEntityName || !viewerIsTeamAdmin;

  const getCreateSecretTooltip = () => {
    if (!targetEntityName) {
      return (
        <div className="text-sm max-w-[230px]">
          Select a destination project to create a team secret.
        </div>
      );
    }
    if (!viewerIsTeamAdmin) {
      return (
        <div className="text-sm max-w-[230px]">
          Team secrets can only be created by admins of{' '}
          <strong>{targetEntityName}</strong>, owner of the destination project
          for your launch.
        </div>
      );
    }
    return undefined;
  };

  const shouldDisableSelect = secretsLoading || secretsError || !options.length;
  const createSecretTooltip = getCreateSecretTooltip();

  return (
    <div
      className="m-0 flex flex-col text-sm"
      key={property.key}
      data-test={property.key}>
      <PropertyHeader
        property={property}
        descriptionOverride={
          <React.Fragment>
            {property.description
              ? linkify(property.description, {
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  className: 'font-semibold text-teal-600 hover:text-teal-500',
                })
              : null}{' '}
            Team secrets can be managed in your team settings by team admins.
          </React.Fragment>
        }
        action={
          openSecretsView && (
            <Button
              variant="secondary"
              size="sm"
              startIcon="add-new"
              onPress={openSecretsView}
              data-test="create-secret-button"
              isDisabled={isCreateSecretDisabled}
              tooltip={
                createSecretTooltip
                  ? {children: createSecretTooltip, placement: 'top start'}
                  : undefined
              }>
              Create secret
            </Button>
          )
        }
      />
      <Select<PropertyOption, false>
        id={id}
        options={options}
        value={optionValue}
        onChange={handleChange}
        isDisabled={shouldDisableSelect}
        placeholder={
          <Tailwind>
            <span className="text-sm text-moon-500 dark:text-wb-text-tertiary">
              {getPlaceholderText()}
            </span>
          </Tailwind>
        }
        isClearable={true}
      />
    </div>
  );
};
